import { useEffect, useState } from "react";
import type { DutyRate, LockCategory } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows, motion } from "@/styles/tokens";
import { DetailPanel } from "@/components/DetailPanel";
import { computeDutyPreview } from "@/services/pricing";
import { formatRupees } from "@/lib/format";
import { ReadOnlySave } from "@/components/ReadOnlySave";

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};
const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

const CATEGORIES: LockCategory[] = ["Copper", "Fiber", "Accessory", "All"];

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={LABEL}>{children}</span>;
}

function TextField({
  label, testid, value, onChange, placeholder,
}: { label: string; testid: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} style={INPUT} />
    </div>
  );
}
function NumberField({
  label, testid, value, onChange,
}: { label: string; testid: string; value: number; onChange: (v: number) => void }) {
  const [buf, setBuf] = useState<string>(String(value));
  useEffect(() => { setBuf(String(value)); }, [value]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="number" value={buf}
             onChange={(e) => {
               setBuf(e.target.value);
               const n = parseFloat(e.target.value);
               if (Number.isFinite(n)) onChange(n);
             }}
             style={{ ...INPUT, textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
    </div>
  );
}
function SelectField<T extends string>({
  label, testid, value, options, onChange,
}: { label: string; testid: string; value: T; options: readonly T[]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value as T)} style={INPUT}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function DateField({
  label, testid, value, onChange,
}: { label: string; testid: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="date" value={value}
             onChange={(e) => onChange(e.target.value)} style={INPUT} />
    </div>
  );
}
function ActiveToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <FieldLabel>Active</FieldLabel>
      <button data-testid="duty-active-toggle" onClick={() => onChange(!value)}
              style={{
                width: layout.toggleTrackWidth, height: heights.pill, padding: spacing[2], borderRadius: radius.full,
                border: "none", cursor: "pointer",
                backgroundColor: value ? colors.indigo600 : semanticColors.textDisabled,
                display: "flex", alignItems: "center",
                justifyContent: value ? "flex-end" : "flex-start",
                transition: `background-color ${motion.fast}`,
              }}>
        <span style={{ width: heights.icon, height: heights.icon, borderRadius: "50%",
                       backgroundColor: colors.white, boxShadow: shadows.toggleKnob }} />
      </button>
    </div>
  );
}

export function DutyRateDetailPanel({
  rate, onClose,
}: {
  rate: DutyRate | null;
  onClose: () => void;
}) {
  const [form, setForm] = useState<DutyRate | null>(rate);
  useEffect(() => { setForm(rate); }, [rate]);
  if (!rate || !form) return null;

  const set = <K extends keyof DutyRate>(k: K, v: DutyRate[K]) =>
    setForm((cur) => (cur ? { ...cur, [k]: v } : cur));

  const preview = computeDutyPreview(form);

  return (
    <DetailPanel
      testIdPrefix="duty"
      title={form.hsnCode}
      subtitle={form.description}
      onClose={onClose}
      footer={
        <>
          <button data-testid="duty-panel-cancel" className="btn-secondary" onClick={onClose}
                  style={{ height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
                           color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`,
                           borderRadius: radius.base, fontSize: typography.fontSize.base, cursor: "pointer" }}>
            Cancel
          </button>
          <ReadOnlySave testId="duty-panel-save" />
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <TextField label="HSN Code" testid="dr-hsnCode" value={form.hsnCode}
                     onChange={(v) => set("hsnCode", v)} />
          <TextField label="Description" testid="dr-description" value={form.description}
                     onChange={(v) => set("description", v)} />
        </div>

        <SelectField label="Category" testid="dr-category" value={form.category}
                     options={CATEGORIES} onChange={(v) => set("category", v)} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: spacing[14] }}>
          <NumberField label="BCD %" testid="dr-bcd" value={form.bcdPct}
                       onChange={(v) => set("bcdPct", v)} />
          <NumberField label="SWS %" testid="dr-sws" value={form.swsPct}
                       onChange={(v) => set("swsPct", v)} />
          <NumberField label="IGST %" testid="dr-igst" value={form.igstPct}
                       onChange={(v) => set("igstPct", v)} />
        </div>

        <div
          data-testid="dr-duty-preview"
          style={{ fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder, lineHeight: typography.lineHeight.base }}
        >
          On goods worth Rs 1,00,000: BCD {formatRupees(preview.bcd)}, SWS {formatRupees(preview.sws)}, IGST {formatRupees(preview.igst)} (recoverable). Landed duty cost: {formatRupees(preview.landedDutyCost)}.
        </div>

        <DateField label="Valid From" testid="dr-valid-from" value={form.validFrom}
                   onChange={(v) => set("validFrom", v)} />

        <TextField label="Notes" testid="dr-notes" value={form.notes}
                   onChange={(v) => set("notes", v)}
                   placeholder="e.g. as per Customs notification 50/2017 amended" />

        <ActiveToggle value={form.active} onChange={(v) => set("active", v)} />
      </div>
    </DetailPanel>
  );
}
